import type { LocationAddress } from "@sitescene/types";

import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import { Form } from "@/components/ui/form/form";
import { GeneralModal } from "@/features/modals/general-modal";

import type { ShadowLocation } from "../../types/location-map";

import { useLocationAddressGeocode } from "../../hooks/useLocationAddressGeocode";
import { useLocationFormMap } from "../../hooks/useLocationFormMap";
import { useCreateLocation } from "../../hooks/useMapEntityCreates";
import { useUpdateLocation } from "../../hooks/useMapEntityUpdates";
import { isValidCoordinatePair } from "../../utils/shared/coordinates";
import { LocationEditorFields } from "./shared/location-editor-fields";
import {
	emptyLocationFormValues,
	locationToFormValues,
} from "./shared/location-form-values";
import { MapPlacementSection } from "./shared/map-placement-section";

export type LocationFormProps =
	| (LocationFormBaseProps & {
			location: ShadowLocation;
			mode: "edit";
			onCreated?: never;
	  })
	| (LocationFormBaseProps & {
			location?: never;
			mode: "create";
			onCreated?: (locationId: string) => void;
	  });

type LocationFormBaseProps = {
	mapCenter?: [number, number] | null;
	onClose: () => void;
};

/**
 * Create or edit location dialog (shared fields + map + GeneralModal).
 * Create: mounted by CreateEntityModal. Edit: mounted by EditEntityModal.
 */
export const LocationForm = (props: LocationFormProps) => {
	const { mapCenter, mode, onClose } = props;
	const isEdit = mode === "edit";
	const location = isEdit ? props.location : undefined;

	const createLocation = useCreateLocation();
	const updateLocation = useUpdateLocation();
	const [error, setError] = useState<null | string>(null);

	const {
		control,
		formState: { errors, isValid },
		getValues,
		handleSubmit,
		setValue,
	} = useForm({
		defaultValues: location
			? locationToFormValues(location)
			: emptyLocationFormValues(),
		mode: "onChange" as const,
	});

	const watchedName = useWatch({ control, name: "name" }) ?? "";
	const watchedLatitude = useWatch({ control, name: "latitude" }) ?? "";
	const watchedLongitude = useWatch({ control, name: "longitude" }) ?? "";

	const {
		mapboxAccessToken,
		reverseGeocodeAt,
		shouldCloseOnInteractOutside,
		wrapAddressFields,
	} = useLocationAddressGeocode({
		control,
		getValues,
		setValue,
	});

	const {
		handleMapClick,
		handleMarkerDragEnd,
		handleUseMapCenter,
		hasPartialCoordinates,
		locationMarker,
		mapFrameCenter,
		mapMarkers,
		mapOverlays,
		onMoveEnd,
		viewportCenter,
	} = useLocationFormMap({
		latitude: watchedLatitude,
		location,
		locationId: location?.id ?? "new-location",
		locationName: location?.name,
		longitude: watchedLongitude,
		mapCenter,
		name: watchedName,
		reverseGeocodeAt,
	});

	const isPending = isEdit
		? updateLocation.isPending
		: createLocation.isPending;

	const canSubmit =
		watchedName.trim().length > 0 &&
		!hasPartialCoordinates &&
		isValid &&
		!isPending;

	const onSubmit = handleSubmit((data) => {
		setError(null);

		const lat = parseFloat(data.latitude);
		const lng = parseFloat(data.longitude);
		const hasCoords = isValidCoordinatePair([lng, lat]);

		const nextAddress: LocationAddress = {
			city: data.city.trim() || undefined,
			postalCode: data.postalCode.trim() || undefined,
			stateProvince: data.stateProvince.trim() || undefined,
			street1: data.street1.trim() || undefined,
			street2: data.street2.trim() || undefined,
		};
		const hasAddress = Object.values(nextAddress).some(Boolean);

		const name = data.name.trim();
		const timezone = data.timezone.trim() || undefined;
		const coordinates = hasCoords
			? { coordinates: [lng, lat] as [number, number], type: "Point" as const }
			: undefined;
		const address = hasAddress ? nextAddress : undefined;

		if (isEdit) {
			updateLocation.mutate(
				{
					body: {
						address,
						coordinates,
						name,
						timezone,
					},
					locationId: props.location.id,
				},
				{
					onError: () => {
						setError("Could not save location.");
					},
					onSuccess: () => {
						onClose();
					},
				},
			);
			return;
		}

		createLocation.mutate(
			{
				address,
				coordinates,
				name,
				timezone,
			},
			{
				onError: () => {
					setError("Could not create location.");
				},
				onSuccess: (created) => {
					props.onCreated?.(created.id);
					onClose();
				},
			},
		);
	});

	return (
		<GeneralModal
			closeOnSubmit={false}
			description={
				isEdit
					? "Update name, address, timezone, and map coordinates for this location."
					: "Add a location for this organization. Address and map pin are optional."
			}
			isOpen
			onClose={onClose}
			onSubmit={onSubmit}
			panelClassName="sm:max-w-160"
			shouldCloseOnInteractOutside={shouldCloseOnInteractOutside}
			submitDisabled={!canSubmit}
			submitText={
				isEdit
					? updateLocation.isPending
						? "Saving…"
						: "Save location"
					: createLocation.isPending
						? "Creating…"
						: "Create location"
			}
			title={isEdit ? "Edit location" : "Create location"}
		>
			<Form
				className="flex flex-col gap-4 pb-2"
				onSubmit={onSubmit}
				validationBehavior="aria"
			>
				<LocationEditorFields
					control={control}
					errors={errors}
					mapboxAccessToken={mapboxAccessToken}
					wrapAddressFields={wrapAddressFields}
				/>
				<MapPlacementSection
					center={mapFrameCenter}
					hasPartialCoordinates={hasPartialCoordinates}
					leadingToolbar={
						isEdit ? (
							<p className="text-sm text-tertiary">
								Existing zones appear on the map for reference.
							</p>
						) : undefined
					}
					markers={mapMarkers}
					markersInteractive={Boolean(locationMarker)}
					onMapClick={handleMapClick}
					onMarkerDragEnd={locationMarker ? handleMarkerDragEnd : undefined}
					onMoveEnd={onMoveEnd}
					onUseMapCenter={handleUseMapCenter}
					overlays={mapOverlays}
					showUseMapCenter={Boolean(viewportCenter)}
				/>
				{error ? <p className="text-sm text-error-primary">{error}</p> : null}
			</Form>
		</GeneralModal>
	);
};
